import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/* ═══════════════════════════════════════════════════════════════
   usePostnatalNewborn — Custom hook for Postnatal Care – Newborn (Task 16)
   Manages newborn PNC assessments, growth tracking, immunisation,
   HIV-exposed infant follow-up, and danger sign monitoring.
   ═══════════════════════════════════════════════════════════════ */

const USE_MOCK_DATA = false;

/* ─── Constants ─── */

export const FEEDING_STATUS = [
    { value: "exclusive_breast", label: "Exclusive Breastfeeding" },
    { value: "mixed", label: "Mixed Feeding" },
    { value: "formula", label: "Formula / Replacement Feeding" },
];

export const CORD_STATUS = [
    { value: "clean", label: "Clean — Healing" },
    { value: "infected", label: "Infected (redness, discharge, smell)" },
    { value: "fallen", label: "Fallen Off" },
];

export const JAUNDICE_LEVELS = [
    { value: "none", label: "None" },
    { value: "mild", label: "Mild (face only)" },
    { value: "moderate", label: "Moderate (trunk)" },
    { value: "severe", label: "Severe (palms/soles — REFER)" },
];

export const BREATHING_STATUS = [
    { value: "normal", label: "Normal (30–60 bpm)" },
    { value: "fast", label: "Fast Breathing (≥60 bpm)" },
    { value: "grunting", label: "Grunting / Chest Indrawing" },
];

export const ACTIVITY_STATUS = [
    { value: "active", label: "Active — Good Tone" },
    { value: "lethargic", label: "Lethargic / Floppy" },
];

export const NEWBORN_DANGER_SIGNS = [
    { value: "not_feeding", label: "Not feeding or poor feeding" },
    { value: "convulsions", label: "Convulsions / seizures" },
    { value: "fast_breathing", label: "Fast breathing (≥60/min)" },
    { value: "severe_chest_indrawing", label: "Severe chest indrawing" },
    { value: "fever", label: "Fever (≥37.5°C)" },
    { value: "hypothermia", label: "Hypothermia (<35.5°C)" },
    { value: "no_movement", label: "No movement / very weak" },
    { value: "jaundice", label: "Jaundice (within 24h or severe)" },
    { value: "bleeding", label: "Bleeding from cord or other site" },
    { value: "vomiting", label: "Persistent vomiting" },
    { value: "abdominal_distension", label: "Abdominal distension" },
    { value: "pus_eyes", label: "Pus draining from eyes" },
    { value: "skin_pustules", label: "Skin pustules / blisters" },
    { value: "umbilical_redness", label: "Umbilical redness extending to skin" },
];

export const IMMUNIZATION_OPTIONS = [
    { value: "bcg", label: "BCG", scheduleAge: "At birth" },
    { value: "opv0", label: "OPV-0 (Oral Polio)", scheduleAge: "At birth" },
    { value: "hep_b_birth", label: "Hepatitis B (birth dose)", scheduleAge: "At birth" },
    { value: "opv1", label: "OPV-1", scheduleAge: "6 weeks" },
    { value: "dpt_hep_hib1", label: "DPT-HepB-Hib 1", scheduleAge: "6 weeks" },
    { value: "pcv1", label: "PCV-1", scheduleAge: "6 weeks" },
    { value: "rota1", label: "Rotavirus 1", scheduleAge: "6 weeks" },
];

/* ─── Helper ─── */
const todayDate = function () {
    const d = new Date();
    const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

/* ─── Initial Newborn PNC Form ─── */
const INITIAL_NEWBORN_FORM = {
    pnc_visit_number: "",
    visit_date: todayDate(),
    weight_grams: "",
    temperature: "",
    feeding_status: "",
    cord_status: "",
    jaundice: "none",
    breathing: "normal",
    activity: "active",
    immunizations_given: [],
    danger_signs: [],
    nevirapine_continued: false,
    ctx_started: false,
    dbs_collected: false,
    dbs_result: "",
    notes: "",
};

/* ═══ MOCK DATA ═══ */

const MOCK_NEWBORN = {
    id: 7001,
    delivery_id: 8001,
    mother_patient_number: "MOCK-PT-001",
    baby_patient_number: "MOCK-BABY-001",
    sex: "female",
    birth_weight_grams: 3200,
    birth_length_cm: 49,
    head_circumference_cm: 34,
    apgar_1min: 8,
    apgar_5min: 9,
    outcome: "alive",
    bcg_given: true,
    opv0_given: true,
    hep_b_birth_dose: true,
    nevirapine_given: false,
    breastfeeding_within_1hr: true,
};

const MOCK_MOTHER_BABY_PAIR = {
    id: 6001,
    mother_patient_number: "MOCK-PT-001",
    baby_patient_number: "MOCK-BABY-001",
    delivery_id: 8001,
    newborn_id: 7001,
    pair_status: "active",
};

const MOCK_NEWBORN_PNC_VISITS = [
    {
        id: 201,
        newborn_id: 7001,
        pnc_visit_number: 1,
        visit_date: "2026-02-28",
        weight_grams: 3150,
        temperature: 36.7,
        feeding_status: "exclusive_breast",
        cord_status: "clean",
        jaundice: "none",
        breathing: "normal",
        activity: "active",
        immunizations_given: ["bcg", "opv0", "hep_b_birth"],
        danger_signs: [],
        nevirapine_continued: false,
        ctx_started: false,
        dbs_collected: false,
        dbs_result: "",
        notes: "Baby breastfeeding well. Normal examination.",
        seen_by: "Dr. Sarah Nakato",
        created_at: "2026-02-28T15:30:00",
    },
];

/* ═══ HOOK ═══ */
export const usePostnatalNewborn = function (actions, patient_number, visit_id, deliveryRecord, onSaveSuccess) {

    /* ─── Newborn record ─── */
    const nbState = useState(null);
    const newborn = nbState[0];
    const setNewborn = nbState[1];

    /* ─── Mother-baby pair ─── */
    const pairState = useState(null);
    const motherBabyPair = pairState[0];
    const setMotherBabyPair = pairState[1];

    /* ─── PNC visits ─── */
    const visitsState = useState([]);
    const pncVisits = visitsState[0];
    const setPncVisits = visitsState[1];

    /* ─── Form state ─── */
    const formState = useState(Object.assign({}, INITIAL_NEWBORN_FORM));
    const form = formState[0];
    const setForm = formState[1];

    /* ─── Editing visit ─── */
    const editState = useState(null);
    const editingVisit = editState[0];
    const setEditingVisit = editState[1];

    /* ─── HIV-exposed ─── */
    const hivExposed = useMemo(function () {
        if (!newborn) return false;
        return newborn.nevirapine_given === true;
    }, [newborn]);

    /* ─── UI state ─── */
    const loadState = useState(true);
    const loading = loadState[0];
    const setLoading = loadState[1];

    const savingState = useState(false);
    const saving = savingState[0];
    const setSaving = savingState[1];

    /* ─── Fetch data ─── */
    useEffect(function () {
        if (!patient_number || !visit_id) return;
        setLoading(true);
        if (USE_MOCK_DATA) {
            setTimeout(function () {
                setNewborn(MOCK_NEWBORN);
                setMotherBabyPair(MOCK_MOTHER_BABY_PAIR);
                setPncVisits(MOCK_NEWBORN_PNC_VISITS);
                /* Set next visit number */
                const nextNum = MOCK_NEWBORN_PNC_VISITS.length + 1;
                if (nextNum <= 4) {
                    setForm(function (prev) {
                        return Object.assign({}, prev, { pnc_visit_number: String(nextNum) });
                    });
                }
                setLoading(false);
            }, 400);
            return;
        }
        /* Real API calls */
        const deliveryId = deliveryRecord ? deliveryRecord.id : null;
        let fetchChain;
        if (deliveryId) {
            fetchChain = axios.get(config.smsUrl + "/obgyn/newborns/" + deliveryId)
                .then(function (res) {
                    const newborns = res.data || [];
                    const first = newborns[0] || null;
                    setNewborn(first);
                    return first;
                });
        } else {
            fetchChain = axios.get(config.smsUrl + "/obgyn/mother-baby/" + patient_number)
                .then(function (res) {
                    const pairs = res.data || [];
                    if (pairs.length > 0) {
                        setMotherBabyPair(pairs[0]);
                        return axios.get(config.smsUrl + "/obgyn/newborn/" + pairs[0].newborn_id)
                            .then(function (nbRes) {
                                setNewborn(nbRes.data || null);
                                return nbRes.data || null;
                            });
                    }
                    return null;
                });
        }
        fetchChain.then(function (nb) {
            if (nb && nb.id) {
                return axios.get(config.smsUrl + "/obgyn/pnc/newborn/" + nb.id);
            }
            return null;
        }).then(function (res) {
            if (res && res.data) {
                const visits = Array.isArray(res.data) ? res.data : (res.data.visits || []);
                setPncVisits(visits);
                const nextNum = visits.length + 1;
                if (nextNum <= 4) {
                    setForm(function (prev) {
                        return Object.assign({}, prev, { pnc_visit_number: String(nextNum) });
                    });
                }
            }
        }).catch(function () { }).finally(function () { setLoading(false); });
    }, [patient_number, visit_id, deliveryRecord]);

    /* ─── Form change handler ─── */
    const handleChange = useCallback(function (e) {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(function (prev) {
            const next = Object.assign({}, prev);
            next[name] = value;
            return next;
        });
    }, []);

    /* ─── Toggle immunization ─── */
    const toggleImmunization = useCallback(function (immValue) {
        setForm(function (prev) {
            const current = prev.immunizations_given || [];
            const idx = current.indexOf(immValue);
            let next;
            if (idx >= 0) {
                next = current.filter(function (i) { return i !== immValue; });
            } else {
                next = current.concat([immValue]);
            }
            return Object.assign({}, prev, { immunizations_given: next });
        });
    }, []);

    /* ─── Toggle danger sign ─── */
    const toggleDangerSign = useCallback(function (signValue) {
        setForm(function (prev) {
            const current = prev.danger_signs || [];
            const idx = current.indexOf(signValue);
            let next;
            if (idx >= 0) {
                next = current.filter(function (s) { return s !== signValue; });
            } else {
                next = current.concat([signValue]);
            }
            return Object.assign({}, prev, { danger_signs: next });
        });
    }, []);

    /* ─── Weight trend ─── */
    const weightTrend = useMemo(function () {
        if (!newborn) return [];
        const trend = [{ visit: "Birth", weight: newborn.birth_weight_grams, date: "" }];
        pncVisits.forEach(function (v) {
            if (v.weight_grams) {
                trend.push({
                    visit: "PNC " + v.pnc_visit_number,
                    weight: parseInt(v.weight_grams, 10),
                    date: v.visit_date,
                });
            }
        });
        return trend;
    }, [newborn, pncVisits]);

    /* ─── Weight loss alert ─── */
    const weightAlert = useMemo(function () {
        if (!newborn || !form.weight_grams) return null;
        const birthWeight = newborn.birth_weight_grams;
        const currentWeight = parseInt(form.weight_grams, 10);
        if (!currentWeight || !birthWeight) return null;
        const percentChange = ((currentWeight - birthWeight) / birthWeight) * 100;
        /* Normal: up to 10% loss in first week, then should regain */
        if (percentChange < -10) {
            return {
                type: "critical",
                title: "Significant Weight Loss",
                detail: "Weight " + currentWeight + "g is " + Math.abs(percentChange).toFixed(1) + "% below birth weight (" + birthWeight + "g)",
                action: "Assess feeding adequacy. Check for illness. Consider admission for observation.",
            };
        }
        if (percentChange < -7) {
            return {
                type: "warning",
                title: "Weight Loss Concern",
                detail: "Weight " + currentWeight + "g is " + Math.abs(percentChange).toFixed(1) + "% below birth weight (" + birthWeight + "g)",
                action: "Support breastfeeding. Reassess feeding technique. Follow up in 48 hours.",
            };
        }
        return null;
    }, [newborn, form.weight_grams]);

    /* ─── Alerts ─── */
    const alerts = useMemo(function () {
        const list = [];

        if (weightAlert) list.push(weightAlert);

        /* Temperature */
        const temp = parseFloat(form.temperature);
        if (temp >= 37.5) {
            list.push({
                type: "critical",
                title: "Neonatal Fever",
                detail: "Temperature " + temp + "°C — possible neonatal sepsis",
                action: "REFER: Blood cultures. Start empirical antibiotics. Monitor closely.",
            });
        } else if (temp < 35.5 && temp > 0) {
            list.push({
                type: "critical",
                title: "Hypothermia",
                detail: "Temperature " + temp + "°C — neonatal hypothermia",
                action: "Warm baby (skin-to-skin). Ensure room temperature. Monitor for sepsis.",
            });
        }

        /* Severe jaundice */
        if (form.jaundice === "severe") {
            list.push({
                type: "critical",
                title: "Severe Jaundice — REFER",
                detail: "Jaundice extending to palms and soles",
                action: "URGENT REFERRAL for phototherapy / exchange transfusion. Check bilirubin.",
            });
        } else if (form.jaundice === "moderate") {
            list.push({
                type: "warning",
                title: "Moderate Jaundice",
                detail: "Jaundice extending to trunk",
                action: "Expose to indirect sunlight. Encourage frequent feeding. Reassess in 24 hours.",
            });
        }

        /* Breathing */
        if (form.breathing === "grunting") {
            list.push({
                type: "critical",
                title: "Respiratory Distress",
                detail: "Grunting / chest indrawing detected",
                action: "URGENT: Assess for neonatal sepsis, RDS, pneumonia. Consider referral to NICU.",
            });
        } else if (form.breathing === "fast") {
            list.push({
                type: "warning",
                title: "Fast Breathing",
                detail: "Respiratory rate ≥60/min",
                action: "Keep warm. Observe for 1 hour. If persistent, investigate for sepsis/pneumonia.",
            });
        }

        /* Activity */
        if (form.activity === "lethargic") {
            list.push({
                type: "critical",
                title: "Lethargic Baby",
                detail: "Baby is lethargic / floppy — possible serious illness",
                action: "Assess for sepsis, meningitis, hypoglycaemia. Consider referral.",
            });
        }

        /* Cord infection */
        if (form.cord_status === "infected") {
            list.push({
                type: "warning",
                title: "Cord Infection (Omphalitis)",
                detail: "Signs of umbilical cord infection present",
                action: "Local antiseptic care. Oral antibiotics. Monitor for spread (cellulitis, sepsis).",
            });
        }

        /* Danger signs */
        if (form.danger_signs && form.danger_signs.length > 0) {
            list.push({
                type: "critical",
                title: "Neonatal Danger Signs Detected",
                detail: form.danger_signs.length + " danger sign(s) identified",
                action: "Immediate assessment. Consider referral for neonatal care. Document action taken.",
            });
        }

        /* Feeding */
        if (form.feeding_status === "formula" || form.feeding_status === "mixed") {
            list.push({
                type: "info",
                title: "Suboptimal Feeding",
                detail: form.feeding_status === "formula" ? "Baby is not breastfeeding" : "Mixed feeding in place",
                action: "Counsel on benefits of exclusive breastfeeding. Address challenges. Refer to lactation support.",
            });
        }

        /* HIV-exposed infant reminders */
        if (hivExposed) {
            if (!form.nevirapine_continued) {
                list.push({
                    type: "warning",
                    title: "HIV-Exposed: Nevirapine Check",
                    detail: "Ensure nevirapine prophylaxis is being continued",
                    action: "Verify daily nevirapine. Counsel mother on importance of adherence.",
                });
            }
            /* At 6 weeks */
            const visitNum = parseInt(form.pnc_visit_number, 10);
            if (visitNum === 4) {
                if (!form.dbs_collected) {
                    list.push({
                        type: "warning",
                        title: "HIV-Exposed: DBS Due at 6 Weeks",
                        detail: "Dried blood spot (DBS) for early infant diagnosis should be collected",
                        action: "Collect DBS for HIV PCR. Start CTX prophylaxis.",
                    });
                }
                if (!form.ctx_started) {
                    list.push({
                        type: "warning",
                        title: "HIV-Exposed: Start CTX at 6 Weeks",
                        detail: "Cotrimoxazole prophylaxis should be initiated at 6 weeks",
                        action: "Start CTX prophylaxis. Continue until HIV status confirmed negative.",
                    });
                }
            }
        }

        return list;
    }, [form, weightAlert, hivExposed]);

    /* ─── Days of life ─── */
    const daysOfLife = useMemo(function () {
        if (!newborn) return null;
        /* Use delivery record or newborn creation date */
        const birthDate = deliveryRecord
            ? new Date(deliveryRecord.delivery_datetime)
            : new Date(newborn.created_at);
        const now = new Date();
        return Math.floor((now.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
    }, [newborn, deliveryRecord]);

    /* ─── Edit existing visit ─── */
    const startEditVisit = useCallback(function (visit) {
        setEditingVisit(visit);
        setForm(Object.assign({}, INITIAL_NEWBORN_FORM, visit, {
            pnc_visit_number: String(visit.pnc_visit_number),
        }));
    }, []);

    /* ─── Reset form ─── */
    const resetForm = useCallback(function () {
        setEditingVisit(null);
        const nextNum = pncVisits.length + 1;
        setForm(Object.assign({}, INITIAL_NEWBORN_FORM, {
            pnc_visit_number: nextNum <= 4 ? String(nextNum) : "",
        }));
    }, [pncVisits]);

    /* ─── Save PNC newborn visit ─── */
    const savePncVisit = useCallback(function () {
        if (!form.pnc_visit_number) {
            actions.snackbarActions.snackbarOpen("error", "Please select a PNC visit number");
            return;
        }
        if (!form.visit_date) {
            actions.snackbarActions.snackbarOpen("error", "Please enter the visit date");
            return;
        }
        setSaving(true);
        const payload = Object.assign({}, form, {
            newborn_id: newborn ? newborn.id : null,
            mother_baby_pair_id: motherBabyPair ? motherBabyPair.id : null,
            visit_id: visit_id,
            pnc_visit_number: parseInt(form.pnc_visit_number, 10),
        });

        if (USE_MOCK_DATA) {
            setTimeout(function () {
                if (editingVisit) {
                    const updated = Object.assign({}, editingVisit, payload);
                    setPncVisits(function (prev) {
                        return prev.map(function (v) { return v.id === editingVisit.id ? updated : v; });
                    });
                    actions.snackbarActions.snackbarOpen("success", "Newborn PNC visit updated (mock)");
                } else {
                    const saved = Object.assign({}, payload, {
                        id: Date.now(),
                        seen_by: "Current User",
                        created_at: new Date().toISOString(),
                    });
                    setPncVisits(function (prev) { return prev.concat([saved]); });
                    actions.snackbarActions.snackbarOpen("success", "Newborn PNC visit saved (mock)");
                }
                resetForm();
                setSaving(false);
                if (typeof onSaveSuccess === "function") onSaveSuccess();
            }, 300);
            return;
        }

        const request = editingVisit
            ? axios.put(config.smsUrl + "/obgyn/pnc/newborn/" + editingVisit.id, payload)
            : axios.post(config.smsUrl + "/obgyn/pnc/newborn", payload);

        request
            .then(function (res) {
                const data = res.data || payload;
                if (editingVisit) {
                    setPncVisits(function (prev) {
                        return prev.map(function (v) { return v.id === editingVisit.id ? data : v; });
                    });
                } else {
                    setPncVisits(function (prev) { return prev.concat([data]); });
                }
                actions.snackbarActions.snackbarOpen("success",
                    editingVisit ? "Newborn PNC visit updated" : "Newborn PNC visit saved");
                resetForm();
                if (typeof onSaveSuccess === "function") onSaveSuccess();
            })
            .catch(function (err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to save newborn PNC visit";
                actions.snackbarActions.snackbarOpen("error", msg);
            })
            .finally(function () { setSaving(false); });
    }, [form, newborn, motherBabyPair, visit_id, editingVisit, actions, resetForm, onSaveSuccess]);

    /* ─── Summary ─── */
    const summary = useMemo(function () {
        return {
            totalVisits: pncVisits.length,
            latestWeight: pncVisits.length > 0 ? pncVisits[pncVisits.length - 1].weight_grams : (newborn ? newborn.birth_weight_grams : null),
            hasDangerSigns: pncVisits.some(function (v) { return v.danger_signs && v.danger_signs.length > 0; }),
            immunizationsGiven: pncVisits.reduce(function (acc, v) {
                return acc.concat(v.immunizations_given || []);
            }, []),
            feedingStatus: pncVisits.length > 0 ? pncVisits[pncVisits.length - 1].feeding_status : null,
        };
    }, [pncVisits, newborn]);

    return {
        /* Constants */
        FEEDING_STATUS: FEEDING_STATUS,
        CORD_STATUS: CORD_STATUS,
        JAUNDICE_LEVELS: JAUNDICE_LEVELS,
        BREATHING_STATUS: BREATHING_STATUS,
        ACTIVITY_STATUS: ACTIVITY_STATUS,
        NEWBORN_DANGER_SIGNS: NEWBORN_DANGER_SIGNS,
        IMMUNIZATION_OPTIONS: IMMUNIZATION_OPTIONS,
        /* Data */
        newborn: newborn,
        motherBabyPair: motherBabyPair,
        pncVisits: pncVisits,
        hivExposed: hivExposed,
        daysOfLife: daysOfLife,
        weightTrend: weightTrend,
        /* Form */
        form: form,
        setForm: setForm,
        handleChange: handleChange,
        toggleImmunization: toggleImmunization,
        toggleDangerSign: toggleDangerSign,
        editingVisit: editingVisit,
        /* Computed */
        alerts: alerts,
        weightAlert: weightAlert,
        summary: summary,
        /* Actions */
        savePncVisit: savePncVisit,
        startEditVisit: startEditVisit,
        resetForm: resetForm,
        /* UI */
        loading: loading,
        saving: saving,
    };
};
